/**
 * Concrete Pump Scheduling — pumps as schedulable assets, distinct from
 * mixer dispatch (gap-analysis build). Backend: routers/pumps.py.
 */
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Plus, X, Loader2, Wrench, Calendar, ChevronLeft, ChevronRight, Gauge,
} from 'lucide-react';
import PageHeader from '../components/PageHeader';
import { apiGet, apiPost, apiPatch } from '../lib/api';
import { inrCompact, formatISTDateTime } from '../lib/format';

const TABS = ['Schedule', 'Jobs', 'Pumps'];
const PUMP_KINDS = ['Boom Pump', 'Stationary / Line Pump', 'Mini Pump'];
const JOB_STATUS_COLOR = {
  scheduled: 'bg-slate-100 text-slate-700',
  dispatched: 'bg-amber-100 text-amber-700',
  pumping: 'bg-teal-50 text-[#1F7A6E]',
  completed: 'bg-emerald-100 text-emerald-700',
  cancelled: 'bg-red-100 text-red-700',
};

export default function Pumps() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTabState] = useState(TABS.includes(searchParams.get('tab')) ? searchParams.get('tab') : 'Schedule');
  useEffect(() => {
    const t = searchParams.get('tab');
    if (t && TABS.includes(t) && t !== tab) setTabState(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);
  const setTab = (t) => { setTabState(t); setSearchParams(t === 'Schedule' ? {} : { tab: t }); };
  return (
    <div data-testid="pumps-page">
      <PageHeader
        overline="Sales & Dispatch"
        title="Pump Scheduling"
        subtitle="Book concrete pumps for pours — separate from mixer/truck dispatch."
      />
      <div className="bg-white border border-slate-200 rounded-sm">
        <div className="border-b border-slate-200 flex overflow-x-auto">
          {TABS.map((t) => (
            <button key={t} onClick={() => setTab(t)}
              className={'relative px-5 py-3 text-sm font-display font-semibold whitespace-nowrap transition-colors ' + (tab === t ? 'text-[#0F172A]' : 'text-slate-500 hover:text-[#0F172A]')}
              data-testid={`pumps-tab-${t.toLowerCase()}`}>
              {t}
              {tab === t && <span className="absolute bottom-0 left-3 right-3 h-0.5 bg-[#1F7A6E]" />}
            </button>
          ))}
        </div>
        <div className="p-5">
          {tab === 'Schedule' && <ScheduleTab />}
          {tab === 'Jobs' && <JobsTab />}
          {tab === 'Pumps' && <PumpsTab />}
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return <div><label className="label">{label}</label>{children}</div>;
}

function ModalShell({ title, onClose, children, wide }) {
  return (
    <div className="fixed inset-0 z-[1000] bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className={`bg-white rounded-sm w-full ${wide ? 'max-w-2xl' : 'max-w-md'} max-h-[90vh] overflow-y-auto`} onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white">
          <div className="font-display font-bold text-sm">{title}</div>
          <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-700"><X size={16} /></button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

function todayISO() { return new Date().toISOString().slice(0, 10); }

// ── Schedule board ───────────────────────────────────────────────────────────
function ScheduleTab() {
  const [date, setDate] = useState(todayISO());
  const [data, setData] = useState(null);
  const [showBook, setShowBook] = useState(false);
  const [detail, setDetail] = useState(null);

  const load = () => apiGet('/pumps/schedule', { date }).then(setData).catch(() => toast.error('Failed to load schedule'));
  useEffect(() => { load(); }, [date]); // eslint-disable-line react-hooks/exhaustive-deps

  const shiftDay = (delta) => {
    const d = new Date(date);
    d.setDate(d.getDate() + delta);
    setDate(d.toISOString().slice(0, 10));
  };

  if (!data) return <div className="text-slate-400 py-8 text-center text-sm">Loading…</div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <button onClick={() => shiftDay(-1)} className="p-1.5 border border-slate-200 rounded-sm hover:bg-slate-50"><ChevronLeft size={14} /></button>
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="input w-auto" data-testid="schedule-date" />
        <button onClick={() => shiftDay(1)} className="p-1.5 border border-slate-200 rounded-sm hover:bg-slate-50"><ChevronRight size={14} /></button>
        <button onClick={() => setDate(todayISO())} className="text-xs text-[#1F7A6E] hover:underline ml-1">Today</button>
        <div className="ml-auto text-xs text-slate-400">{data.jobs_total} booking(s) this day</div>
        <button onClick={() => setShowBook(true)}
          className="px-4 py-2 bg-[#1F7A6E] hover:bg-[#14504E] text-white text-xs font-display font-bold uppercase tracking-widest rounded-sm flex items-center gap-2"
          data-testid="pump-book-btn">
          <Plus size={13} /> Book Pump
        </button>
      </div>

      {data.pumps.length === 0 && (
        <div className="text-slate-400 py-10 text-center text-sm">No pumps registered yet — add one in the Pumps tab.</div>
      )}

      <div className="space-y-3">
        {data.pumps.map((p) => (
          <div key={p.id} className="border border-slate-200 rounded-sm overflow-hidden">
            <div className="bg-slate-50 px-3 py-2 flex items-center justify-between border-b border-slate-200">
              <div>
                <span className="font-display font-bold text-sm">{p.pump_no}</span>
                <span className="text-xs text-slate-500 ml-2">{p.kind}{p.boom_length_m ? ` · ${p.boom_length_m}m boom` : ''}</span>
              </div>
              <span className={`tag ${p.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>{p.status}</span>
            </div>
            {p.jobs.length === 0 ? (
              <div className="px-3 py-3 text-xs text-slate-400">Free all day</div>
            ) : (
              <div className="divide-y divide-slate-100">
                {p.jobs.map((j) => (
                  <button key={j.id} onClick={() => setDetail(j)}
                    className="w-full text-left px-3 py-2 flex items-center justify-between hover:bg-slate-50 text-sm"
                    data-testid={`pump-job-${j.id}`}>
                    <div className="flex items-center gap-3">
                      <span className="font-mono text-xs text-slate-500">{new Date(j.start_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</span>
                      <span className="font-medium">{j.party_name}</span>
                      <span className="text-xs text-slate-400">{j.jobsite || ''}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-slate-500">{j.expected_hours}h{j.expected_qty_m3 ? ` · ${j.expected_qty_m3} m³` : ''}</span>
                      <span className={`tag ${JOB_STATUS_COLOR[j.status]}`}>{j.status}</span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {showBook && <BookJobModal defaultDate={date} onClose={() => setShowBook(false)} onSaved={() => { setShowBook(false); load(); }} />}
      {detail && <JobDetailModal job={detail} onClose={() => setDetail(null)} onUpdated={() => { setDetail(null); load(); }} />}
    </div>
  );
}

function BookJobModal({ defaultDate, onClose, onSaved }) {
  const [pumps, setPumps] = useState([]);
  const [form, setForm] = useState({
    pump_id: '', party_name: '', jobsite: '', site_address: '', contact_name: '', contact_phone: '',
    date: defaultDate, time: '09:00', expected_hours: '4', expected_qty_m3: '', grade: '',
    rate_type: 'per_m3', rate: '', notes: '',
  });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => { apiGet('/pumps').then((r) => setPumps((r.items || []).filter((p) => p.status === 'active'))); }, []);

  const save = async () => {
    if (!form.pump_id) return toast.error('Select a pump');
    if (!form.party_name.trim()) return toast.error('Customer / party name is required');
    setSaving(true);
    try {
      const job = await apiPost('/pumps/jobs', {
        pump_id: form.pump_id, party_name: form.party_name, jobsite: form.jobsite || null,
        site_address: form.site_address || null, contact_name: form.contact_name || null,
        contact_phone: form.contact_phone || null,
        start_at: `${form.date}T${form.time}:00`,
        expected_hours: Number(form.expected_hours || 4),
        expected_qty_m3: form.expected_qty_m3 ? Number(form.expected_qty_m3) : null,
        grade: form.grade || null, rate_type: form.rate_type, rate: Number(form.rate || 0),
        notes: form.notes || null,
      });
      toast.success(`${job.job_no} booked`);
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to book pump'); }
    finally { setSaving(false); }
  };

  return (
    <ModalShell title="Book Pump" onClose={onClose} wide>
      <div className="space-y-3">
        <Field label="Pump">
          <select value={form.pump_id} onChange={set('pump_id')} className="input" autoFocus>
            <option value="">Select…</option>
            {pumps.map((p) => <option key={p.id} value={p.id}>{p.pump_no} — {p.kind}</option>)}
          </select>
          {pumps.length === 0 && <div className="text-xs text-amber-600 mt-1">No active pumps — add one in the Pumps tab first.</div>}
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Customer / Party"><input value={form.party_name} onChange={set('party_name')} className="input" placeholder="Aditya Builders" /></Field>
          <Field label="Jobsite"><input value={form.jobsite} onChange={set('jobsite')} className="input" /></Field>
        </div>
        <Field label="Site Address"><input value={form.site_address} onChange={set('site_address')} className="input" /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Site Contact"><input value={form.contact_name} onChange={set('contact_name')} className="input" /></Field>
          <Field label="Contact Phone"><input value={form.contact_phone} onChange={set('contact_phone')} className="input" /></Field>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <Field label="Date"><input type="date" value={form.date} onChange={set('date')} className="input" /></Field>
          <Field label="Start Time"><input type="time" value={form.time} onChange={set('time')} className="input" /></Field>
          <Field label="Expected Hours"><input type="number" value={form.expected_hours} onChange={set('expected_hours')} className="input" /></Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Grade"><input value={form.grade} onChange={set('grade')} className="input" placeholder="M25" /></Field>
          <Field label="Expected Qty (m³)"><input type="number" value={form.expected_qty_m3} onChange={set('expected_qty_m3')} className="input" /></Field>
        </div>
        <div className="grid grid-cols-2 gap-3 pt-2 border-t border-slate-100">
          <Field label="Rate Type">
            <select value={form.rate_type} onChange={set('rate_type')} className="input">
              <option value="per_m3">Per m³</option>
              <option value="per_hour">Per Hour</option>
              <option value="lumpsum">Lumpsum</option>
            </select>
          </Field>
          <Field label="Rate (₹)"><input type="number" value={form.rate} onChange={set('rate')} className="input" /></Field>
        </div>
        <Field label="Notes"><input value={form.notes} onChange={set('notes')} className="input" /></Field>
        <button onClick={save} disabled={saving} className="btn-primary w-full justify-center mt-2">
          {saving ? <Loader2 size={14} className="animate-spin" /> : 'Book Pump'}
        </button>
      </div>
    </ModalShell>
  );
}

function JobDetailModal({ job, onClose, onUpdated }) {
  const [saving, setSaving] = useState(false);
  const [actual, setActual] = useState({ actual_start: '', actual_end: '', actual_qty_m3: job.actual_qty_m3 || '' });

  const setStatus = async (status) => {
    setSaving(true);
    try {
      await apiPatch(`/pumps/jobs/${job.id}`, { status });
      toast.success(`Marked ${status}`);
      onUpdated();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to update'); }
    finally { setSaving(false); }
  };

  const complete = async () => {
    setSaving(true);
    try {
      await apiPatch(`/pumps/jobs/${job.id}`, {
        status: 'completed',
        actual_start: actual.actual_start || null,
        actual_end: actual.actual_end || null,
        actual_qty_m3: actual.actual_qty_m3 ? Number(actual.actual_qty_m3) : null,
      });
      toast.success('Job completed');
      onUpdated();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to complete'); }
    finally { setSaving(false); }
  };

  return (
    <ModalShell title={job.job_no} onClose={onClose} wide>
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div><div className="text-slate-400 text-xs">Pump</div>{job.pump_no} · {job.pump_kind}</div>
          <div><div className="text-slate-400 text-xs">Status</div><span className={`tag ${JOB_STATUS_COLOR[job.status]}`}>{job.status}</span></div>
          <div><div className="text-slate-400 text-xs">Customer</div>{job.party_name}</div>
          <div><div className="text-slate-400 text-xs">Jobsite</div>{job.jobsite || '—'}</div>
          <div><div className="text-slate-400 text-xs">Start</div>{formatISTDateTime(job.start_at)}</div>
          <div><div className="text-slate-400 text-xs">Expected Duration</div>{job.expected_hours} h</div>
          <div><div className="text-slate-400 text-xs">Grade / Qty</div>{job.grade || '—'} · {job.expected_qty_m3 ? `${job.expected_qty_m3} m³` : '—'}</div>
          <div><div className="text-slate-400 text-xs">Rate</div>{job.rate ? `${inrCompact(job.rate)} (${job.rate_type.replace('_', ' ')})` : '—'}</div>
          <div><div className="text-slate-400 text-xs">Site Contact</div>{job.contact_name || '—'} {job.contact_phone ? `· ${job.contact_phone}` : ''}</div>
        </div>
        {job.notes && <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-sm p-2">{job.notes}</div>}

        {job.status === 'completed' && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-3 text-sm space-y-1">
            <div className="kpi-overline">Actuals</div>
            <div>{job.hours_run ? `${job.hours_run} h run` : ''} {job.actual_qty_m3 ? `· ${job.actual_qty_m3} m³ pumped` : ''}</div>
          </div>
        )}

        {!['completed', 'cancelled'].includes(job.status) && (
          <div className="space-y-3 border-t border-slate-100 pt-3">
            <div className="flex gap-2">
              {job.status === 'scheduled' && <button onClick={() => setStatus('dispatched')} disabled={saving} className="btn-secondary flex-1 justify-center">Mark Dispatched</button>}
              {job.status === 'dispatched' && <button onClick={() => setStatus('pumping')} disabled={saving} className="btn-secondary flex-1 justify-center">Start Pumping</button>}
              <button onClick={() => setStatus('cancelled')} disabled={saving} className="btn-secondary flex-1 justify-center">Cancel</button>
            </div>
            <div className="border-t border-slate-100 pt-3">
              <div className="kpi-overline mb-2">Complete Job</div>
              <div className="grid grid-cols-3 gap-2">
                <Field label="Actual Start"><input type="datetime-local" value={actual.actual_start} onChange={(e) => setActual({ ...actual, actual_start: e.target.value })} className="input text-xs" /></Field>
                <Field label="Actual End"><input type="datetime-local" value={actual.actual_end} onChange={(e) => setActual({ ...actual, actual_end: e.target.value })} className="input text-xs" /></Field>
                <Field label="Qty Pumped (m³)"><input type="number" value={actual.actual_qty_m3} onChange={(e) => setActual({ ...actual, actual_qty_m3: e.target.value })} className="input text-xs" /></Field>
              </div>
              <button onClick={complete} disabled={saving} className="btn-primary w-full justify-center mt-2">
                {saving ? <Loader2 size={14} className="animate-spin" /> : 'Complete Job'}
              </button>
            </div>
          </div>
        )}
      </div>
    </ModalShell>
  );
}

// ── Jobs list ─────────────────────────────────────────────────────────────────
function JobsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);

  const load = () => {
    setLoading(true);
    apiGet('/pumps/jobs').then((r) => setItems(r.items || []))
      .catch(() => toast.error('Failed to load pump jobs')).finally(() => setLoading(false));
  };
  useEffect(load, []);

  return (
    <div>
      <div className="kpi-overline mb-3">All Bookings · {items.length}</div>
      <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
        <table className="w-full dense-table">
          <thead><tr>
            <th>Job No</th><th>Start</th><th>Pump</th><th>Customer</th>
            <th>Jobsite</th><th className="text-right">Hours</th><th>Status</th>
          </tr></thead>
          <tbody>
            {loading && <tr><td colSpan="7" className="text-center py-8 text-slate-400">Loading…</td></tr>}
            {!loading && items.length === 0 && <tr><td colSpan="7" className="text-center py-8 text-slate-400">No pump bookings yet.</td></tr>}
            {items.map((j) => (
              <tr key={j.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setDetail(j)} data-testid={`pump-job-row-${j.id}`}>
                <td className="font-mono text-xs text-[#1F7A6E] font-semibold">{j.job_no}</td>
                <td className="text-xs">{formatISTDateTime(j.start_at)}</td>
                <td className="font-display font-bold">{j.pump_no}</td>
                <td>{j.party_name}</td>
                <td className="text-xs text-slate-500">{j.jobsite || '—'}</td>
                <td className="text-right tabular-nums">{j.expected_hours}</td>
                <td><span className={`tag ${JOB_STATUS_COLOR[j.status]}`}>{j.status}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {detail && <JobDetailModal job={detail} onClose={() => setDetail(null)} onUpdated={() => { setDetail(null); load(); }} />}
    </div>
  );
}

// ── Pumps register ───────────────────────────────────────────────────────────
function PumpsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);

  const load = () => {
    setLoading(true);
    apiGet('/pumps').then((r) => setItems(r.items || []))
      .catch(() => toast.error('Failed to load pumps')).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const toggleStatus = async (p) => {
    const next = p.status === 'active' ? 'inactive' : 'active';
    try {
      await apiPatch(`/pumps/${p.id}`, { status: next });
      toast.success(`${p.pump_no} marked ${next}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to update'); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="kpi-overline">Pump Register · {items.length}</div>
        <button onClick={() => setShowAdd(true)}
          className="px-4 py-2 bg-[#1F7A6E] hover:bg-[#14504E] text-white text-xs font-display font-bold uppercase tracking-widest rounded-sm flex items-center gap-2"
          data-testid="pump-add-btn">
          <Plus size={13} /> Add Pump
        </button>
      </div>
      <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
        <table className="w-full dense-table">
          <thead><tr>
            <th>Pump No</th><th>Kind</th><th>Capacity</th><th>Operator</th>
            <th>Ownership</th><th>Status</th><th></th>
          </tr></thead>
          <tbody>
            {loading && <tr><td colSpan="7" className="text-center py-8 text-slate-400">Loading…</td></tr>}
            {!loading && items.length === 0 && <tr><td colSpan="7" className="text-center py-8 text-slate-400">No pumps registered yet.</td></tr>}
            {items.map((p) => (
              <tr key={p.id} data-testid={`pump-row-${p.id}`}>
                <td className="font-mono text-xs font-semibold">{p.pump_no}</td>
                <td>{p.kind}</td>
                <td className="text-xs text-slate-500">{p.capacity_m3hr ? `${p.capacity_m3hr} m³/hr` : ''}{p.boom_length_m ? ` · ${p.boom_length_m}m boom` : ''}</td>
                <td className="text-xs">{p.operator_name || '—'}</td>
                <td className="capitalize text-xs">{p.ownership}</td>
                <td><span className={`tag ${p.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>{p.status}</span></td>
                <td className="text-right">
                  <button onClick={() => toggleStatus(p)} className="text-xs text-[#1F7A6E] hover:underline">
                    {p.status === 'active' ? 'Deactivate' : 'Activate'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {showAdd && <AddPumpModal onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); load(); }} />}
    </div>
  );
}

function AddPumpModal({ onClose, onSaved }) {
  const [form, setForm] = useState({
    pump_no: '', kind: 'Boom Pump', capacity_m3hr: '', boom_length_m: '',
    operator_name: '', operator_phone: '', ownership: 'owned', remarks: '',
  });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    if (!form.pump_no.trim()) return toast.error('Pump number is required');
    setSaving(true);
    try {
      await apiPost('/pumps', {
        ...form,
        capacity_m3hr: form.capacity_m3hr ? Number(form.capacity_m3hr) : null,
        boom_length_m: form.boom_length_m ? Number(form.boom_length_m) : null,
      });
      toast.success('Pump registered');
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to add pump'); }
    finally { setSaving(false); }
  };

  return (
    <ModalShell title="Add Pump" onClose={onClose}>
      <div className="space-y-3">
        <Field label="Pump No"><input value={form.pump_no} onChange={set('pump_no')} className="input" placeholder="PUMP-01" autoFocus /></Field>
        <Field label="Kind">
          <select value={form.kind} onChange={set('kind')} className="input">
            {PUMP_KINDS.map((k) => <option key={k}>{k}</option>)}
          </select>
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Capacity (m³/hr)"><input type="number" value={form.capacity_m3hr} onChange={set('capacity_m3hr')} className="input" /></Field>
          <Field label="Boom Length (m)"><input type="number" value={form.boom_length_m} onChange={set('boom_length_m')} className="input" /></Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Operator Name"><input value={form.operator_name} onChange={set('operator_name')} className="input" /></Field>
          <Field label="Operator Phone"><input value={form.operator_phone} onChange={set('operator_phone')} className="input" /></Field>
        </div>
        <Field label="Ownership">
          <select value={form.ownership} onChange={set('ownership')} className="input">
            <option value="owned">Owned</option>
            <option value="hired">Hired</option>
          </select>
        </Field>
        <Field label="Remarks"><input value={form.remarks} onChange={set('remarks')} className="input" /></Field>
        <button onClick={save} disabled={saving} className="btn-primary w-full justify-center mt-2">
          {saving ? <Loader2 size={14} className="animate-spin" /> : 'Add Pump'}
        </button>
      </div>
    </ModalShell>
  );
}
